import React, { useEffect, useState } from 'react';
import { UserIcon, CpuChipIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { VoiceModal, ModalFooter, Field, Select, TextInput, TEXT_MUTED, TEXT_PRIMARY } from '../components';
import { formatPhoneNumber } from '../components/format';
import { DEPARTMENTS } from './constants';

/**
 * Assign a number to a user, an AI agent, or a department.
 *
 * The original was 204 lines inline, with the confirm button's colour and
 * focus ring switching between blue, purple and amber depending on the
 * selected type — the only place in the module that didn't use the orange
 * accent. The type is already communicated by the selected card; the action
 * button doesn't need to restate it in a third colour.
 */
const TYPES = [
  { id: 'user', label: 'User', icon: UserIcon, hint: 'A person on your team' },
  { id: 'agent', label: 'AI agent', icon: CpuChipIcon, hint: 'An AI that answers calls' },
  { id: 'department', label: 'Department', icon: BuildingOfficeIcon, hint: 'A shared queue' },
];

const AssignModal = ({ open, did, users = [], agents = [], onCancel, onAssign, saving }) => {
  const [type, setType] = useState('user');
  const [target, setTarget] = useState('');
  const [extension, setExtension] = useState('');

  // Reset when the modal opens for a different number, otherwise the previous
  // selection leaks into the next assignment.
  useEffect(() => {
    if (open) {
      setType('user');
      setTarget('');
      setExtension('');
    }
  }, [open, did?._id]);

  const options = {
    user: users.map((u) => ({
      value: u._id,
      label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
      sub: u.email,
    })),
    agent: agents.map((a) => ({
      value: a._id,
      label: a.agentName || 'Unnamed agent',
      sub: a.department || a.role,
    })),
    department: DEPARTMENTS.map((d) => ({ value: d, label: d.toUpperCase() })),
  }[type];

  return (
    <VoiceModal
      open={open}
      onClose={onCancel}
      title="Assign number"
      subtitle={did ? formatPhoneNumber(did.phoneNumber) : undefined}
      footer={
        <ModalFooter
          onCancel={onCancel}
          onConfirm={() => onAssign({ type, target, extension })}
          confirmLabel="Assign"
          loading={saving}
          disabled={!target}
        />
      }
    >
      <div className="space-y-5">
        <div>
          <p className={`text-[13px] font-medium mb-2 ${TEXT_PRIMARY}`}>Assign to</p>
          <div className="grid grid-cols-3 gap-2">
            {TYPES.map((t) => {
              const active = type === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => {
                    setType(t.id);
                    setTarget('');
                  }}
                  className={`p-3 rounded-xl border text-left transition-colors ${
                    active
                      ? 'border-orange-500 bg-orange-500/[0.06]'
                      : 'border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/[0.16]'
                  }`}
                >
                  <t.icon
                    className={`w-4 h-4 mb-2 ${active ? 'text-orange-500' : 'text-gray-400'}`}
                  />
                  <span className={`block text-[13px] font-medium ${TEXT_PRIMARY}`}>{t.label}</span>
                  <span className={`block text-[11px] mt-0.5 ${TEXT_MUTED}`}>{t.hint}</span>
                </button>
              );
            })}
          </div>
        </div>

        <Field
          label={type === 'department' ? 'Department' : type === 'agent' ? 'Agent' : 'User'}
          required
        >
          <Select value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">
              {options.length ? `Select a ${type}…` : `No ${type}s available`}
            </option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
                {o.sub ? ` — ${o.sub}` : ''}
              </option>
            ))}
          </Select>
        </Field>

        {/* Extension only applies to a person. */}
        {type === 'user' && (
          <Field label="Extension" hint="Optional internal extension for this user">
            <TextInput
              value={extension}
              onChange={(e) => setExtension(e.target.value)}
              placeholder="e.g. 101"
              inputMode="numeric"
            />
          </Field>
        )}
      </div>
    </VoiceModal>
  );
};

export default AssignModal;
